import { lazy, Suspense, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import {
  ArrowUpRight, ArrowRight,
  Search, Megaphone, Video, TrendingUp, MonitorSmartphone, Target, Activity,
  Home as HomeIcon, HardHat, MapPin, Workflow,
} from 'lucide-react';
import SEO from '@/components/SEO';
import Reveal, { Stagger, StaggerItem } from '@/components/Reveal';
import GrowthEngine from '@/components/GrowthEngine';
import Testimonials from '@/components/Testimonials';
import FAQ from '@/components/FAQ';
import StickyMobileCTA from '@/components/StickyMobileCTA';
import GrowthPrompt from '@/components/GrowthPrompt';
import { ResponsivePhoto } from '@/components/IndustryVisual';
import { Overline } from '@/components/primitives';
import { ORG_JSONLD, WEBSITE_JSONLD, faqLd, SITE, RENOVATION_TILES, BRAND } from '@/lib/site';
import { TOOLS } from '@/pages/toolsData';
import { SERVICES } from '@/pages/servicesData';
import { trackEvent, EVENTS } from '@/lib/analytics';

// Below the fold and data-heavy (every industry's copy; every article's full text).
// Loaded as their own chunks so the first paint, and every other page, does not pay
// for them. Each placeholder holds roughly the section's height so nothing jumps.
const IndustriesExperience = lazy(() => import('@/components/IndustriesExperience'));
const HomeResources = lazy(() => import('@/components/HomeResources'));

const HOME_FAQS = [
  {
    q: 'What does HyenaMind actually do?',
    a: 'HyenaMind builds and runs the growth system behind a local business: website and landing pages, Google Business Profile and local SEO, Google, Meta and TikTok Ads, conversion and tracking, lead qualification, follow-up and automation. One connected system rather than six disconnected suppliers.',
  },
  {
    q: 'Which businesses is this built for?',
    a: 'Local contractors and home-service businesses: construction, renovation, roofing, HVAC, plumbing, electrical, painting, cleaning, landscaping, pest control and similar trades. The growth problem is the same across them, even when the work is not.',
  },
  {
    q: 'How is HyenaMind different from a typical marketing agency?',
    a: 'Most agencies sell one stage. A website, or ads, or SEO. The gaps between those stages are where the money leaks: traffic that lands on a weak page, enquiries nobody qualifies, leads nobody follows up. HyenaMind is built to own the whole path from visibility to booked work.',
  },
  {
    q: 'Do you guarantee rankings, leads or results?',
    a: 'No reputable growth partner guarantees rankings, lead counts or revenue, and we do not either. What we commit to is a system built for how local businesses actually win work, transparent reporting and continuous optimisation toward qualified opportunities.',
  },
  {
    q: 'What makes a lead "qualified"?',
    a: 'Every enquiry is checked against the service you offer, the area you work in and whether the intent is real and current. Anything that fails those checks is not passed on as a job. Quality over volume, by design.',
  },
  {
    q: 'Can I start with one service instead of the whole system?',
    a: 'Yes. Most businesses start where the biggest gap is, usually the website or local visibility, then add stages as each one earns its keep. The system is designed to be built in order, not bought all at once.',
  },
  {
    q: 'Where does HyenaMind operate?',
    a: 'HyenaMind works with local businesses in their own service areas and is built to operate across markets. Tell us where you work and we will confirm what we can support.',
  },
];

const HOMEOWNER_JOURNEY = [
  { t: 'Have a project', d: 'Kitchen, bathroom, basement, full home or something else entirely.' },
  { t: 'Understand your options', d: 'Honest guides and cost context before you talk to anyone.' },
  { t: 'Plan', d: 'Free tools for scope, budget and preparation.' },
  { t: 'Compare', d: 'Quote options side by side, with the differences made clear.' },
  { t: 'Find professionals', d: 'Suitable local professionals matched to your project.' },
  { t: 'Request quotes', d: 'One request. Real, comparable options. No obligation.' },
];

const LADDER = [
  { t: 'Traffic', d: 'Attention from search and social' },
  { t: 'Lead', d: 'A homeowner raises their hand' },
  { t: 'Qualified lead', d: 'Reviewed for real project intent. The HyenaMind baseline.', mark: true },
  { t: 'Appointment', d: 'A scheduled conversation, where applicable' },
  { t: 'Job', d: 'Won by the contractor. Never guaranteed, always earned.' },
];

const FRAMEWORK_RAIL = [
  { title: 'Build', text: 'Website, landing pages, conversion UX' },
  { title: 'Get found', text: 'Google Business Profile, local SEO' },
  { title: 'Attract', text: 'Google, Meta and TikTok Ads' },
  { title: 'Convert', text: 'Landing pages, CRO, tracking' },
  { title: 'Qualify', text: 'Qualification, routing, scheduling' },
  { title: 'Follow up', text: 'CRM, email and SMS, reminders' },
  { title: 'Grow', text: 'Automation, reporting, optimisation' },
];

/* Capability signal under the hero CTA. One icon family, no partner or
   certification logos: every item is a service HyenaMind actually delivers. */
const CAPABILITIES = [
  { icon: MonitorSmartphone, label: 'Web Development' },
  { icon: MapPin, label: 'Local SEO' },
  { icon: Search, label: 'Google Ads' },
  { icon: Megaphone, label: 'Meta Ads' },
  { icon: Video, label: 'TikTok Ads' },
  { icon: Target, label: 'Lead Generation' },
  { icon: Activity, label: 'Tracking' },
  { icon: Workflow, label: 'Automation' },
];

const PAID_CHANNELS = [
  {
    id: 'google',
    icon: Search,
    name: 'Google Ads',
    role: 'Capture existing intent',
    arrival: 'Paid search · already looking',
    path: '/kitchen-renovations',
    param: '?utm_source=google&utm_medium=cpc',
    text: 'Homeowners already searching for a renovation professional, matched to real search intent and filtered by negative keywords.',
    to: '/contractors/google-ads',
  },
  {
    id: 'meta',
    icon: Megaphone,
    name: 'Meta Ads',
    role: 'Reach and retarget',
    arrival: 'Paid social · reached, then retargeted',
    path: '/kitchen-renovation-offer',
    param: '?utm_source=meta&utm_medium=paid_social',
    text: 'Facebook and Instagram audiences built around renovation intent, keeping finished work visible before someone starts searching.',
    to: '/contractors/meta-ads',
  },
  {
    id: 'tiktok',
    icon: Video,
    name: 'TikTok Ads',
    role: 'Create discovery',
    arrival: 'Short-form video · earlier stage',
    path: '/recent-projects',
    param: '?utm_source=tiktok&utm_medium=paid_social',
    text: 'Earlier-stage attention through short-form video, on a platform where renovation work is genuinely watched.',
    to: '/services/social-media-marketing',
  },
  {
    id: 'seo',
    icon: TrendingUp,
    name: 'SEO',
    role: 'Build long-term visibility',
    arrival: 'Organic search · no cost per click',
    path: '/kitchen-renovations-toronto',
    param: '',
    text: 'Organic and local visibility that keeps working between campaigns, built on real service pages and local signals.',
    to: '/contractors/seo',
  },
];

/* The full journey. The split is the point: most providers stop at stage three. */
const AFTER_CLICK = [
  { t: 'Lead', d: 'A real enquiry, captured and attributed to the channel that produced it.' },
  { t: 'Qualification', d: 'Checked for project type, area and genuine intent before it counts.' },
  { t: 'Follow-up', d: 'Contacted while the enquiry is still warm, not days later.' },
  { t: 'Growth', d: 'Reporting shows what worked, and the next cycle is built on it.' },
];


const ECOSYSTEM_CHAIN = [
  { label: 'Homeowner', icon: HomeIcon },
  { label: 'Renovation project' },
  { label: 'HyenaMind', brand: true },
  { label: 'Relevant contractor', icon: HardHat },
  { label: 'Real opportunity' },
];

const FEATURED_TOOLS = ['renovation-planner', 'compare-renovation-quotes', 'renovation-budget-planner', 'renovation-checklist'];

/* ------------------------------------------------------------------ HERO */

function Hero() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', reduce ? '0%' : '12%']);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.08]);
  // The headline and paragraph are the first thing read (and the LCP element), so
  // they paint at once: the entrance is a short settle, never a hidden start.
  const line = {
    hidden: { y: '0.12em' },
    show: (i) => ({ y: '0em', transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 } }),
  };

  return (
    <section ref={ref} data-testid="hero-audience-router" className="dark-section relative overflow-hidden">
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-0"
        aria-hidden="true"
      >
        {/* object-cover in a tall section renders this ~1600 CSS px wide at every
            breakpoint, so it ships at full size; WebP halves the bytes. */}
        <picture>
          <source srcSet="/images/service-van-driveway.webp" type="image/webp" />
          <img
            src="/images/service-van-driveway.jpg"
            width="1600"
            height="1067"
            alt=""
            className="h-full w-full object-cover"
            decoding="async"
          />
        </picture>
      </motion.div>
      {/* Below lg the copy runs the full width, so the right of the frame is
          scrimmed as deeply as the left; from lg it clears for the photograph. */}
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-navy/25 max-lg:via-60% max-lg:to-navy/60" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-navy/45" aria-hidden="true" />

      <div className="container-wide relative pt-28 sm:pt-44 lg:pt-52 pb-0">
        <div className="max-w-[64rem]">
          <motion.p
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="overline-tag text-silver"
          >
            <span className="h-px w-8 bg-steel-light" aria-hidden="true" />
            Contractor &amp; local business growth
          </motion.p>
          <h1 className="mt-9 font-display text-[2.75rem] leading-[0.95] sm:text-[3.75rem] lg:text-[4.5rem] xl:text-[5.25rem] tracking-tighter font-medium text-bone">
            {['Stop renting leads.', 'Start owning your growth.'].map((l, i) => (
              <span key={l} className="block overflow-hidden pb-2">
                <motion.span className="block" custom={i} variants={line} initial={reduce ? false : 'hidden'} animate="show">
                  {i === 1 ? <span className="text-silver">{l}</span> : l}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={reduce ? false : { y: 8 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 max-w-2xl text-lg sm:text-xl leading-relaxed text-silver"
          >
From your website and local presence to paid advertising, qualified leads and booked appointments,
            HyenaMind helps contractors and local businesses build a stronger customer acquisition system.
          </motion.p>
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.85 }}
            className="mt-9 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
          >
            <Link
              to="/contractors/grow-your-business"
              data-testid="hero-cta-growth"
              onClick={() => trackEvent(EVENTS.growthCta, { location: 'hero' })}
              className="btn-hero-primary !px-8 !py-[1.15rem] !text-base group w-full sm:w-auto"
            >
              Build My Growth System
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-services"
              className="btn-ghost-light group w-full sm:w-auto"
            >
              Hire Us For One Service
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>

          {/* Capability signal: what the growth system is actually made of. */}
          <motion.ul
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3.5"
            data-testid="hero-capabilities"
          >
            {CAPABILITIES.map((c) => (
              <li key={c.label} className="flex items-center gap-2.5">
                <c.icon className="h-4 w-4 shrink-0 text-steel-light" strokeWidth={1.75} aria-hidden="true" />
                <span className="text-[13px] font-bold tracking-wide text-silver">{c.label}</span>
              </li>
            ))}
          </motion.ul>

          <motion.div
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-8 flex flex-wrap gap-x-7 gap-y-2 text-sm"
          >
            <Link
              to="/contractors/get-qualified-leads"
              data-testid="hero-cta-leads"
              onClick={() => trackEvent(EVENTS.contractorLeadCta, { location: 'hero' })}
              className="inline-flex items-center py-2.5 font-bold text-silver/90 hover:text-bone transition-colors underline underline-offset-4 decoration-white/25 hover:decoration-bone"
            >
              Get Qualified Leads
            </Link>
            <Link
              to="/get-renovation-quotes"
              data-testid="hero-cta-homeowners"
              onClick={() => trackEvent(EVENTS.homeownerQuoteCta, { location: 'hero' })}
              className="inline-flex items-center py-2.5 font-bold text-silver/90 hover:text-bone transition-colors underline underline-offset-4 decoration-white/25 hover:decoration-bone"
            >
              Get Renovation Quotes
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={reduce ? false : { opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 1.25 }}
          className="mt-10 sm:mt-24 lg:mt-28 border-t border-white/15"
          data-testid="hero-framework-rail"
        >
          <div className="flex snap-x snap-mandatory overflow-x-auto sm:overflow-visible sm:grid sm:grid-cols-4 lg:grid-cols-7 divide-x divide-white/10 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {FRAMEWORK_RAIL.map((s, i) => (
              <a
                key={s.title}
                href="#growth-system"
                className="group block w-[62%] shrink-0 snap-start px-4 py-6 sm:w-auto sm:shrink sm:px-5 sm:py-8 first:pl-0 transition-colors hover:bg-white/[0.04]"
                data-testid={`hero-rail-${s.title.toLowerCase().replace(' ', '-')}`}
              >
                <span className="font-display text-xs font-semibold tracking-[0.2em] text-silver">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <p className="mt-2.5 font-display text-lg sm:text-xl font-medium text-bone">{s.title}</p>
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-silver/70">{s.text}</p>
              </a>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}


/* ----------------------------------------------------------- CHOOSE PATH */

/* Increasing weight, not three equal cards: the system should read as the
   larger commitment, and a single service as an ordinary thing to buy. */
const PATHS = [
  {
    key: 'A',
    title: 'I need a specific service',
    text: 'A website, local visibility, a campaign, qualification, appointments or automation. Hire us for the one you need. Nothing here requires the rest.',
    to: '/services',
    linkLabel: 'See all services',
    enquire: '/contact?intent=contractor_growth',
    event: EVENTS.growthCta,
  },
  {
    key: 'B',
    title: 'I need more qualified leads',
    text: 'You already have a website and a provider, and the problem is the pipeline. We work on what produces enquiries and what happens to them next.',
    to: '/contractors/get-qualified-leads',
    linkLabel: 'How lead flow works',
    enquire: '/contact?intent=contractor_leads',
    event: EVENTS.contractorLeadCta,
  },
];

function ChoosePathSection() {
  return (
    <section className="bg-white py-24 sm:py-32" data-testid="choose-path-section">
      <div className="container-site grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <Reveal className="lg:col-span-4">
          <Overline>Choose your path</Overline>
          <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-[3.1rem] font-medium tracking-tight leading-[1.04] sm:leading-[1.04] text-navy">
            Hire us for one thing.<br className="hidden sm:block" /> Or for the whole system.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-navy/60">
            Most people arrive needing one specific thing. That is a normal way to work with us, and it stays
            that way. The complete system is an option, never a condition.
          </p>
          <Link
            to="/growth-assessment"
            data-testid="path-assessment-link"
            className="group mt-7 inline-flex items-center gap-2.5 border-b-2 border-navy/20 py-2 text-[15px] font-bold text-navy transition-colors hover:border-steel hover:text-steel"
          >
            Not sure which? Find your bottleneck
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </Reveal>

        <div className="lg:col-span-8">
          {PATHS.map((path, i) => (
            <Reveal key={path.key} delay={0.08 * (i + 1)}>
              <div
                className="border-t border-navy/15 pt-8 pb-10"
                data-testid={`path-${path.key.toLowerCase()}`}
              >
                <div className="flex items-baseline gap-4">
                  <span className="font-display text-sm font-bold tracking-[0.2em] text-steel">{path.key}</span>
                  <h3 className="font-display text-2xl sm:text-[1.75rem] font-medium tracking-tight text-navy">
                    {path.title}
                  </h3>
                </div>
                <p className="mt-3 max-w-2xl text-base leading-relaxed text-navy/60">{path.text}</p>

                {path.key === 'A' && (
                  <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
                    {SERVICES.map((svc) => (
                      <li key={svc.slug}>
                        <Link
                          to={`/services/${svc.slug}`}
                          data-testid={`path-a-service-${svc.slug}`}
                          className="group flex items-center justify-between gap-3 border-b border-navy/10 py-2.5 text-[15px] text-navy/75 transition-colors hover:text-steel focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-steel"
                        >
                          {svc.name}
                          <ArrowRight className="h-3.5 w-3.5 shrink-0 text-navy/25 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-steel" aria-hidden="true" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-6 flex flex-wrap items-center gap-x-7 gap-y-3">
                  <Link
                    to={path.to}
                    data-testid={`path-${path.key.toLowerCase()}-primary`}
                    onClick={() => trackEvent(path.event, { path: path.key })}
                    className="inline-flex items-center gap-2 text-sm font-bold text-navy transition-colors hover:text-steel"
                  >
                    {path.linkLabel}
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                  <Link
                    to={path.enquire}
                    data-testid={`path-${path.key.toLowerCase()}-enquire`}
                    className="text-sm font-medium text-navy/60 underline underline-offset-4 transition-colors hover:text-navy"
                  >
                    Or enquire directly
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}

          {/* C carries the weight, because it is the larger commitment. */}
          <Reveal delay={0.24}>
            <div className="rounded-md bg-navy p-8 sm:p-10" data-testid="path-c">
              <div className="flex items-baseline gap-4">
                <span className="font-display text-sm font-bold tracking-[0.2em] text-silver">C</span>
                <h3 className="font-display text-2xl sm:text-[1.75rem] font-medium tracking-tight text-bone">
                  I want the complete growth system
                </h3>
              </div>
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-silver">
                Website, local presence, advertising, conversion, qualification, follow-up and optional
                appointment setting and automation, built to hand off to each other instead of running as
                separate efforts.
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-x-7 gap-y-3">
                <Link
                  to="/contractors/grow-your-business"
                  data-testid="path-c-primary"
                  onClick={() => trackEvent(EVENTS.growthCta, { path: 'C' })}
                  className="btn-accent"
                >
                  Build My Growth System
                </Link>
                <Link
                  to="/how-it-works"
                  data-testid="path-c-secondary"
                  className="text-sm font-bold text-bone transition-colors hover:text-steel-light"
                >
                  See how the seven stages connect
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- BIG IDEA */

const CONVERGENCE = ['Website', 'Google', 'Local SEO', 'Paid Ads', 'Social', 'Leads', 'Follow-up', 'Automation'];

function BigIdeaSection() {
  return (
    <section className="bg-bone py-24 sm:py-32 lg:py-40" data-testid="big-idea-section">
      <div className="container-wide">
        <Reveal>
          <h2 className="max-w-5xl font-display text-[2.6rem] sm:text-5xl lg:text-6xl xl:text-7xl tracking-tighter leading-[1.0] font-medium text-navy">
            Your business doesn't grow<br className="hidden sm:block" /> in one channel.
          </h2>
        </Reveal>

        {/* The convergence itself, at statement scale: eight inputs, one outcome. */}
        <div className="mt-16 lg:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-0 items-stretch">
          <Reveal delay={0.1} className="lg:col-span-8">
            <ul className="grid grid-cols-2 sm:grid-cols-4 border-t border-navy/15" data-testid="convergence-inputs">
              {CONVERGENCE.map((p) => (
                <li
                  key={p}
                  className="border-b border-r border-navy/15 px-4 py-6 sm:px-5 sm:py-8 last:border-r-0 sm:[&:nth-child(4n)]:border-r-0"
                >
                  <span className="font-display text-lg sm:text-xl lg:text-2xl font-medium tracking-tight text-navy/70">
                    {p}
                  </span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.2} className="lg:col-span-4">
            <div className="relative flex h-full flex-col justify-center bg-navy px-8 py-12 sm:px-10 lg:py-0">
              <span
                className="absolute left-0 top-1/2 hidden h-px w-10 -translate-x-full -translate-y-1/2 bg-navy/25 lg:block"
                aria-hidden="true"
              />
              <ArrowRight className="h-7 w-7 text-steel-light lg:hidden" aria-hidden="true" />
              <span className="mt-4 block font-display text-5xl sm:text-6xl lg:text-[4.5rem] font-semibold tracking-tighter leading-none text-bone lg:mt-0">
                Growth
              </span>
              <p className="mt-5 max-w-xs text-base leading-relaxed text-silver">
                Not one of these. All of them, connected, and measured against the same outcome.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <p className="mt-16 max-w-3xl text-lg sm:text-xl leading-relaxed text-navy/65">
            Separately, each piece underperforms. A website nobody finds. Ads pointed at a page that does not
            convert. Enquiries nobody qualifies, and leads nobody follows up. Most providers sell one piece at a
            time. HyenaMind connects them into a single system built for local businesses.
          </p>
        </Reveal>
      </div>
    </section>
  );
}


function GrowthSystemSection() {
  return (
    <section id="growth-system" className="dark-section py-24 sm:py-36 lg:py-44 scroll-mt-20" data-testid="growth-section">
      <div className="container-wide">
        <GrowthEngine />
      </div>
    </section>
  );
}

/* ------------------------------------------- TRAFFIC IS ONLY HALF THE JOB */

/* A large, real-looking contractor site. Structure and copy only: no invented
   metrics, no fabricated dashboard, no numbers that would imply results.
   The address bar reflects the selected channel, because the landing page and
   the attribution parameter are the part of this that actually differs. */
function WebsiteVisual({ channel }) {
  const reduce = useReducedMotion();
  return (
    <div className="relative" data-testid="website-visual">
      <div className="rounded-md border border-navy/12 bg-white shadow-[0_40px_90px_rgba(10,17,40,0.16)] overflow-hidden">
        <div className="flex items-center gap-1.5 border-b border-navy/10 bg-bone px-5 py-3.5">
          <span className="h-2.5 w-2.5 rounded-full bg-navy/15" aria-hidden="true" />
          <span className="h-2.5 w-2.5 rounded-full bg-navy/15" aria-hidden="true" />
          <span className="h-2.5 w-2.5 rounded-full bg-navy/15" aria-hidden="true" />
          <span className="ml-4 flex h-7 flex-1 items-center overflow-hidden rounded-sm border border-navy/10 bg-white px-3">
            <AnimatePresence mode="wait">
              <motion.span
                key={channel.id}
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, y: -5 }}
                transition={{ duration: 0.28 }}
                className="truncate font-mono text-[11px] text-navy/60"
                data-testid="website-visual-url"
              >
                yourcompany.ca{channel.path}
                <span className="text-steel">{channel.param}</span>
              </motion.span>
            </AnimatePresence>
          </span>
        </div>

        <div className="flex items-center gap-2.5 border-b border-navy/10 bg-white px-5 py-3">
          <channel.icon className="h-4 w-4 shrink-0 text-steel" strokeWidth={1.75} aria-hidden="true" />
          <p className="text-[12px] text-navy/70" aria-live="polite">
            <span className="font-bold text-navy">Arriving from {channel.name}</span>
            <span className="hidden sm:inline"> — {channel.arrival}</span>
          </p>
        </div>

        <div className="relative h-56 sm:h-72 lg:h-[20rem] overflow-hidden">
          <img
            src="/images/derived/kitchen-3-800.jpg"
            srcSet="/images/derived/kitchen-3-400.jpg 400w, /images/derived/kitchen-3-800.jpg 800w"
            sizes="(min-width: 1024px) 480px, 92vw"
                  width="1600"
                  height="1067"
            alt="A contractor website leading with completed renovation work and a clear request-a-quote action"
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
          {/* Deeper below sm, where the headline wraps to two lines and climbs
              into the brighter top of the photograph. */}
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/90 to-navy/40 sm:via-navy/80 sm:to-navy/20" aria-hidden="true" />
          <div className="absolute inset-x-0 bottom-0 p-6 sm:p-9">
            <p className="font-display text-xl sm:text-3xl font-medium tracking-tight text-bone">
              Kitchen renovations, done properly.
            </p>
            <p className="mt-2 max-w-md text-sm sm:text-base text-silver">
              Fifteen years of finished work across the region.
            </p>
            <span className="mt-5 inline-flex h-11 items-center rounded-md bg-steel px-6 text-[13px] font-bold tracking-wide text-bone">
              Request a quote
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-navy/10 border-t border-navy/10">
          {['Trust', 'SEO', 'Conversion', 'Tracking'].map((p) => (
            <div key={p} className="px-4 py-5 sm:py-6">
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-navy/60">{p}</span>
            </div>
          ))}
        </div>
      </div>
      <p className="mt-3 text-[11px] font-medium uppercase tracking-[0.16em] text-navy/60" data-testid="website-visual-disclaimer">
        Illustrative layout — not a client website
      </p>


      <div
        className="absolute -bottom-8 -right-4 sm:-right-10 w-24 sm:w-32 rounded-md border border-navy/12 bg-white shadow-[0_24px_50px_rgba(10,17,40,0.2)] overflow-hidden hidden sm:block"
        aria-hidden="true"
      >
        <div className="h-24 sm:h-32 overflow-hidden relative">
          <img src="/images/derived/kitchen-3-400.jpg"
                  width="1600"
                  height="1067" alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/85 to-transparent" />
        </div>
        <div className="p-2.5">
          <div className="h-1.5 w-3/4 rounded-full bg-navy/15" />
          <div className="mt-1.5 h-1.5 w-1/2 rounded-full bg-navy/10" />
          <div className="mt-2.5 h-6 w-full rounded-sm bg-steel" />
        </div>
      </div>
    </div>
  );
}

/* Stage three activates one step at a time on entry, so the sequence reads as a
   process rather than appearing all at once. It settles and then stays put. */
function AfterClickRail() {
  const reduce = useReducedMotion();
  const STEP = 0.55;
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel">03 — After the click</p>
      <motion.ol
        className="mt-6 relative space-y-7"
        data-testid="after-click-rail"
        initial={reduce ? false : 'idle'}
        whileInView="live"
        viewport={{ once: true, margin: '-60px' }}
        variants={{ idle: {}, live: { transition: { staggerChildren: STEP } } }}
      >
        <span className="absolute left-[3px] top-2 bottom-2 w-px bg-navy/15" aria-hidden="true" />
        <motion.span
          className="absolute left-[3px] top-2 bottom-2 w-px origin-top bg-steel"
          variants={{ idle: { scaleY: 0 }, live: { scaleY: 1 } }}
          transition={{ duration: reduce ? 0 : STEP * AFTER_CLICK.length, ease: 'linear' }}
          aria-hidden="true"
        />
        {AFTER_CLICK.map((o, i) => (
          <motion.li
            key={o.t}
            className="relative pl-7"
            data-testid={`after-click-${i}`}
            variants={{ idle: { opacity: 0.4 }, live: { opacity: 1 } }}
            transition={{ duration: reduce ? 0 : 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.span
              className="absolute left-0 top-1.5 h-[7px] w-[7px] rounded-full bg-steel ring-4 ring-bone"
              variants={{ idle: { scale: 0.7 }, live: { scale: 1.2 } }}
              transition={{ duration: reduce ? 0 : 0.45, ease: [0.16, 1, 0.3, 1] }}
              aria-hidden="true"
            />
            <p className="font-display text-base font-medium tracking-tight text-navy">{o.t}</p>
            <p className="mt-1 text-[13px] leading-relaxed text-navy/65">{o.d}</p>
          </motion.li>
        ))}
      </motion.ol>
    </div>
  );
}

/* A connector that draws itself in, so the three stages read as one path. */
function StageConnector({ delay = 0 }) {
  const reduce = useReducedMotion();
  return (
    <div className="hidden lg:flex lg:col-span-1 items-center justify-center" aria-hidden="true">
      <motion.span
        className="h-px w-full origin-left bg-navy/25"
        initial={reduce ? false : { scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay }}
      />
      <motion.span
        className="h-1.5 w-1.5 shrink-0 rotate-45 border-t border-r border-navy/40"
        initial={reduce ? false : { opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.3, delay: delay + 0.6 }}
      />
    </div>
  );
}

function AcquisitionSection() {
  const [channelIdx, setChannelIdx] = useState(0);
  const channel = PAID_CHANNELS[channelIdx];

  return (
    <section className="bg-bone py-20 sm:py-28 lg:py-36 overflow-hidden" data-testid="acquisition-section">
      <div className="container-wide">
        <Reveal className="max-w-4xl">
          <Overline>The whole job</Overline>
          <h2 className="mt-6 font-display text-[2.3rem] sm:text-5xl lg:text-6xl xl:text-7xl tracking-tighter leading-[1.02] font-medium text-navy">
            Traffic is only half the job.
          </h2>
          <p className="mt-7 max-w-2xl text-lg sm:text-xl leading-relaxed text-navy/65">
            Getting the right homeowner to the site is where most providers stop. It is also where the work
            actually starts.
          </p>
        </Reveal>

        {/* The split, stated rather than diagrammed. The seven-stage version
            of this lived here and simply restated the Growth Engine above. */}
        <Reveal delay={0.1}>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-x-14 gap-y-8 border-t border-navy/15 pt-9" data-testid="acquisition-split">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-navy/60">
                What most providers sell
              </p>
              <p className="mt-3 font-display text-xl sm:text-2xl font-medium tracking-tight leading-snug text-navy/60">
                Attention, clicks and a monthly traffic report.
              </p>
            </div>
            <div className="relative sm:pl-14">
              <span className="absolute left-0 top-1 hidden h-[calc(100%-0.5rem)] w-px bg-navy/15 sm:block" aria-hidden="true" />
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-steel">
                Where the job is actually won
              </p>
              <p className="mt-3 font-display text-xl sm:text-2xl font-medium tracking-tight leading-snug text-navy">
                What happens after the click — and whether anyone answers it.
              </p>
            </div>
          </div>
        </Reveal>

        {/* Channels → website → what happens next, as one connected composition. */}
        <div className="mt-14 lg:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <Reveal className="lg:col-span-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel">01 — Traffic</p>
            <div className="mt-6 border-t border-navy/15" role="tablist" aria-label="Traffic channels">
              {PAID_CHANNELS.map((c, i) => {
                const on = i === channelIdx;
                return (
                  <button
                    key={c.id}
                    role="tab"
                    aria-selected={on}
                    aria-controls="website-visual-panel"
                    data-testid={`paid-channel-${c.id}`}
                    onClick={() => setChannelIdx(i)}
                    onFocus={() => setChannelIdx(i)}
                    className={`group flex w-full items-center gap-4 border-b border-navy/15 py-4 pl-3 pr-1 text-left transition-colors duration-300 ${
                      on ? 'bg-navy/[0.045]' : 'hover:bg-navy/[0.025]'
                    }`}
                  >
                    <span
                      className={`h-8 w-0.5 shrink-0 rounded-full transition-colors duration-300 ${
                        on ? 'bg-steel' : 'bg-transparent'
                      }`}
                      aria-hidden="true"
                    />
                    <c.icon
                      className={`h-4.5 w-4.5 shrink-0 transition-colors ${on ? 'text-steel' : 'text-navy/50'}`}
                      strokeWidth={1.75}
                    />
                    <span className="flex-1 min-w-0">
                      <span
                        className={`block font-display text-base font-medium tracking-tight transition-colors ${
                          on ? 'text-navy' : 'text-navy/70'
                        }`}
                      >
                        {c.name}
                      </span>
                      <span className="block text-[12px] text-navy/60">{c.role}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="mt-5 text-[13px] leading-relaxed text-navy/60">
              Four different starting points. One place they all have to land.
            </p>
            <Link
              to={channel.to}
              data-testid={`paid-media-${channel.id}`}
              className="group mt-4 inline-flex items-center gap-2 py-2 text-sm font-bold text-navy transition-colors hover:text-steel"
            >
              {channel.name} for contractors
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>

          <StageConnector delay={0.25} />

          <Reveal delay={0.25} className="lg:col-span-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel">02 — The website</p>
            <div className="mt-6" id="website-visual-panel" role="tabpanel">
              <WebsiteVisual channel={channel} />
            </div>
          </Reveal>

          <StageConnector delay={0.5} />

          <Reveal delay={0.5} className="lg:col-span-2">
            <AfterClickRail />
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3">
            <Link
              to="/contractors/web-design"
              data-testid="acquisition-cta-web"
              className="group inline-flex items-center gap-2.5 text-base font-bold text-navy hover:text-steel transition-colors border-b-2 border-navy/20 hover:border-steel py-2"
            >
              See website work
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/services"
              data-testid="acquisition-cta-services"
              className="group inline-flex items-center gap-2 py-2 text-base font-bold text-navy/60 hover:text-steel transition-colors"
            >
              Explore acquisition services
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ------------------------------------------------------- QUALIFIED LEADS */

function ContractorLeadsSection() {
  return (
    <section className="bg-bone py-24 sm:py-36" data-testid="contractor-leads-section">
      <div className="container-wide grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline>Need more qualified opportunities?</Overline>
            <h2 className="mt-6 font-display text-[2.2rem] sm:text-[2.75rem] lg:text-[3.5rem] tracking-tight leading-[1.04] font-medium text-navy">
              More than a lead. A qualified opportunity.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-navy/65">
              A name and a phone number is not an opportunity. Context is. Contractors can build their own growth
              engine, or access qualified homeowner opportunities directly through the wider HyenaMind ecosystem.
              We are honest about what each stage means.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contractors/get-qualified-leads"
                data-testid="leads-section-cta"
                onClick={() => trackEvent(EVENTS.contractorLeadCta, { location: 'home_leads' })}
                className="btn-primary group"
              >
                Get Qualified Leads
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link to="/contractors/renovation-appointments" data-testid="leads-ladder-link" className="btn-ghost-dark group">
                See the full ladder
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>

        {/* Real work, then the ladder: image and content share one composition. */}
        <div className="lg:col-span-7">
          <Reveal delay={0.08}>
            <div className="relative">
              <div className="overflow-hidden rounded-md">
                <img
                  src="/images/construction-1.jpg"
                  width="1600"
                  height="1060"
                  alt="Two workers in hi-vis on a concrete deck among rebar and steel formwork on an active construction site"
                  loading="lazy"
                  decoding="async"
                  className="h-64 sm:h-[24rem] w-full object-cover"
                />
              </div>
              <div className="relative z-10 -mt-14 ml-0 sm:ml-20 bg-navy rounded-md p-7 sm:p-10">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-silver">The opportunity ladder</p>
                <Stagger className="mt-7 flex flex-col">
                  {LADDER.map((l, i) => (
                    <StaggerItem key={l.t}>
                      <div className={`flex items-baseline gap-5 py-3.5 ${i < LADDER.length - 1 ? 'border-b border-white/10' : ''}`}>
                        <span className="font-display text-sm font-semibold tracking-[0.16em] text-silver/70 shrink-0 w-7">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <div>
                          <p
                            className={`font-display text-lg sm:text-xl font-medium tracking-tight ${
                              l.mark ? 'text-silver' : 'text-bone'
                            }`}
                          >
                            {l.t}
                          </p>
                          <p className="mt-0.5 text-sm text-silver/75">{l.d}</p>
                        </div>
                      </div>
                    </StaggerItem>
                  ))}
                </Stagger>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------------------------------- ECOSYSTEM BAND */

function EcosystemBand() {
  return (
    <section className="dark-section py-16 sm:py-20" data-testid="ecosystem-section">
      <div className="container-wide">
        <Reveal>
          {/* The chain needs ~48rem on one line, so it only runs horizontally from lg, and
              only sits beside the heading from xl. Below that it was clipped by the page's
              overflow-x: clip (at 1024px the last two steps were cut off entirely). */}
          <div className="flex flex-col xl:flex-row xl:items-center gap-8 xl:gap-10">
            <div className="xl:w-[22rem] shrink-0">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-silver">
                How the ecosystem connects
              </p>
              <p className="mt-3 font-display text-2xl sm:text-3xl font-medium tracking-tight leading-snug text-bone">
                One homeowner request becomes a contractor's next job.
              </p>
            </div>
            <ol className="flex flex-1 flex-col lg:flex-row lg:items-center gap-2 lg:gap-0" data-testid="ecosystem-diagram">
              {ECOSYSTEM_CHAIN.map((n, i) => (
                <li key={n.label} className="flex items-center gap-2 lg:flex-1 lg:gap-0">
                  <span
                    className={`flex items-center gap-2.5 whitespace-nowrap ${
                      n.brand ? 'font-semibold text-bone' : 'text-silver'
                    }`}
                  >
                    {n.brand ? (
                      <picture>
                        <source srcSet={BRAND.emblemSmallWebp} type="image/webp" />
                        <img src={BRAND.emblemSmall} alt="" className="h-5 w-auto" width="17" height="20" aria-hidden="true" />
                      </picture>
                    ) : (
                      n.icon && <n.icon className="h-4 w-4 shrink-0 text-steel-light" strokeWidth={1.75} />
                    )}
                    <span className="font-display text-[15px] sm:text-base tracking-tight">{n.label}</span>
                  </span>
                  {i < ECOSYSTEM_CHAIN.length - 1 && (
                    <span className="mx-3 hidden flex-1 items-center lg:flex" aria-hidden="true">
                      <span className="h-px w-full bg-white/20" />
                      <span className="h-1 w-1 shrink-0 rotate-45 border-t border-r border-white/35" />
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- HOMEOWNER */

function HomeownerSection() {
  return (
    <section className="relative bg-navy" data-testid="homeowner-section">
      {/* One rectangle, divided. The photograph is the ground and the content
          block is cut into its lower-right corner, flush to both edges, so the
          two share a composition rather than one floating on top of the other. */}
      <div className="relative lg:grid lg:grid-cols-12">
        <div className="relative h-[19rem] sm:h-[26rem] lg:absolute lg:inset-0 lg:h-auto overflow-hidden">
          <img
            src="/images/interior-8.jpg"
                  width="1600"
                  height="1067"
            alt="Open-plan living and dining space opening onto a deck in a completed full-home renovation"
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
          {/* Only as much scrim as the caption needs to stay legible against the
              brightest part of the photograph behind it. The photograph is the
              point; burying it under navy would waste it. */}
          <div
            className="absolute inset-0 bg-gradient-to-br from-navy/90 via-navy/40 to-transparent"
            aria-hidden="true"
          />
          {/* Captioning the photograph is what stops the panel reading as a card
              dropped on top: both halves become labelled parts of one object. */}
          <figcaption className="absolute left-5 top-6 sm:left-8 sm:top-8 lg:left-12 lg:top-12">
            <span className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.22em] text-bone">
              <span className="h-px w-8 bg-steel-light" aria-hidden="true" />
              Full-home renovation
            </span>
            <span className="mt-2 block text-[13px] text-bone">Open-plan living, kitchen and deck</span>
          </figcaption>
        </div>

        {/* Spacer sets how much photograph is left uncovered on desktop. */}
        <div className="hidden lg:block lg:col-span-6 lg:row-start-1 lg:min-h-[43rem]" aria-hidden="true" />

        <Reveal className="relative lg:col-start-7 lg:col-span-6 lg:row-start-1 lg:self-end">
          <div className="relative bg-bone px-6 py-10 sm:px-10 sm:py-12 lg:px-14 lg:py-14">
            {/* The accent rule picks up where the photograph's caption rule
                left off, carrying one line across the whole composition. */}
            <span className="absolute left-0 top-0 h-1 w-24 bg-steel" aria-hidden="true" />
            <Overline>For homeowners</Overline>
            <h2 className="mt-5 max-w-lg font-display text-3xl sm:text-4xl lg:text-[2.35rem] tracking-tight leading-[1.06] font-medium text-navy">
              Your project starts with the right professionals.
            </h2>
            <p className="mt-4 max-w-lg text-[15px] sm:text-base leading-relaxed text-navy/65">
              Describe the project once. We help you reach suitable local professionals and compare real quote
              options, instead of guessing from a search result.
            </p>

            {/* Six steps, stated as steps. No descriptions and no cards: the
                numbering carries the sequence on its own. */}
            <Stagger className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
              {HOMEOWNER_JOURNEY.map((s, i) => (
                <StaggerItem key={s.t}>
                  <div className="flex items-baseline gap-2.5 border-t border-navy/20 pt-3">
                    <span className="font-display text-[11px] font-semibold tracking-[0.16em] text-steel">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <h3 className="font-display text-[15px] font-medium tracking-tight text-navy leading-snug">
                      {s.t}
                    </h3>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>

            <Link
              to="/get-renovation-quotes"
              data-testid="homeowner-section-cta"
              onClick={() => trackEvent(EVENTS.homeownerQuoteCta, { location: 'homeowner_section' })}
              className="group mt-9 inline-flex items-center gap-2.5 text-base font-bold text-navy transition-colors hover:text-steel border-b-2 border-navy/25 hover:border-steel pb-1"
            >
              Get Renovation Quotes
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>

            {/* The tools section used to be 753px of its own. Homeowners are
                the tertiary audience here, so the same links ride along with
                the homeowner block instead of interrupting the services
                argument with a second heading. */}
            <div className="mt-8 border-t border-navy/15 pt-5">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-navy/60">
                Free planning tools
              </span>
              <ul className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
                {FEATURED_TOOLS.map((slug) => {
                  const t = TOOLS.find((x) => x.slug === slug);
                  return (
                    <li key={slug}>
                      <Link
                        to={`/tools/${slug}`}
                        data-testid={`home-tool-${slug}`}
                        className="text-[14px] text-navy/70 underline underline-offset-4 decoration-navy/20 transition-colors hover:text-steel hover:decoration-steel"
                      >
                        {t.name}
                      </Link>
                    </li>
                  );
                })}
                <li>
                  <Link
                    to="/tools"
                    data-testid="tools-section-cta"
                    className="text-[14px] font-bold text-navy transition-colors hover:text-steel"
                  >
                    All tools →
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CategoriesSection() {
  /* Renovation is one vertical, not the subject of the page. This was a
     seven-tile gallery that made the homepage read as a renovation
     marketplace; it is now a compact index that keeps the capability visible
     and hands the depth to /renovations. The three categories with no verified
     photograph no longer occupy prominent tiles. */
  const photographed = RENOVATION_TILES.filter((t) => t.image);
  const rest = RENOVATION_TILES.filter((t) => !t.image);

  return (
    <section className="bg-white py-16 sm:py-20" data-testid="categories-section">
      <div className="container-site">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <Reveal className="lg:col-span-4">
            <Overline>One of the verticals</Overline>
            <h2 className="mt-5 font-display text-3xl sm:text-[2.25rem] tracking-tight leading-[1.08] font-medium text-navy">
              Renovation demand, already in the system.
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-navy/65">
              Homeowners describe a project; we qualify it and route it to a suitable local professional. The
              same qualification that runs behind every other trade.
            </p>
            <Link
              to="/renovations"
              data-testid="categories-section-cta"
              className="group mt-6 inline-flex items-center gap-2.5 border-b-2 border-navy/20 py-2 text-[15px] font-bold text-navy transition-colors hover:border-steel hover:text-steel"
            >
              Explore renovation services
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>

          <div className="lg:col-span-8">
            <Stagger className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {photographed.map((t) => (
                <StaggerItem key={t.slug}>
                  <Link
                    to={`/renovations/${t.slug}`}
                    data-testid={`home-category-${t.slug}`}
                    className="group relative block h-full min-h-[8rem] overflow-hidden rounded-md"
                  >
                    {/* 181px slot: without a sizes hint the browser fetched the 1600px
                        original for each tile. */}
                    <div className="absolute inset-0 transition-transform duration-700 ease-out-expo group-hover:scale-[1.02]">
                      <ResponsivePhoto
                        src={t.image}
                        alt={t.alt}
                        sizes="(min-width: 1024px) 190px, (min-width: 640px) 23vw, 46vw"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/85 to-transparent" aria-hidden="true" />
                    <span className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-3.5">
                      <span className="font-display text-base font-medium tracking-tight text-bone">{t.name}</span>
                      <ArrowUpRight
                        className="h-3.5 w-3.5 shrink-0 text-bone/70 transition-all duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-bone"
                        aria-hidden="true"
                      />
                    </span>
                  </Link>
                </StaggerItem>
              ))}
            </Stagger>

            {/* The remaining categories as a plain ruled index rather than
                empty tiles: present, honest, and visually quiet. */}
            <ul className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-navy/15 pt-4">
              {rest.map((t) => (
                <li key={t.slug}>
                  <Link
                    to={`/renovations/${t.slug}`}
                    data-testid={`home-category-${t.slug}`}
                    className="group inline-flex items-center gap-2 py-2 text-[14px] font-medium text-navy/60 transition-colors hover:text-steel"
                  >
                    {t.name}
                    <ArrowUpRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/renovations"
                  data-testid="home-category-all"
                  className="group inline-flex items-center gap-2 py-2 text-[14px] font-bold text-navy transition-colors hover:text-steel"
                >
                  All renovation services
                  <ArrowUpRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
/* -------------------------------------------------------------- FINAL CTA */

function FinalCTASection() {
  return (
    <section className="dark-section relative overflow-hidden" data-testid="final-cta-section">
      <img
        src="/images/home-exterior-2.jpg"
                  width="1600"
                  height="1342"
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
      />
      {/* Below lg the copy runs the full width over the photograph, so the
          scrim stays deep across it. */}
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/92 to-navy/45 max-lg:via-navy/90 max-lg:via-60% max-lg:to-navy/60" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-transparent to-navy/60" aria-hidden="true" />
      <div className="container-wide py-28 sm:py-44 relative">
        <Reveal className="max-w-3xl">
          <span className="overline-tag text-silver">
            <span className="h-px w-8 bg-steel-light" aria-hidden="true" />
            Ready when you are
          </span>
          <h2 className="mt-6 font-display text-[2.3rem] sm:text-5xl lg:text-6xl xl:text-7xl tracking-tighter leading-[1.02] font-medium text-bone">
            Ready to build a stronger growth system?
          </h2>
          <p className="mt-7 max-w-xl text-lg sm:text-xl leading-relaxed text-silver">
            From your website and local presence to acquisition, conversion, qualification and follow-up,
            HyenaMind connects the pieces that move a local business forward.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/contractors/grow-your-business"
              data-testid="final-cta-growth"
              onClick={() => trackEvent(EVENTS.growthCta, { location: 'final_cta' })}
              className="btn-accent group"
            >
              Build My Growth System
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link to="/services" data-testid="final-cta-services" className="btn-ghost-light group">
              Explore Services
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <Link
              to="/contractors/get-qualified-leads"
              data-testid="final-cta-leads"
              onClick={() => trackEvent(EVENTS.contractorLeadCta, { location: 'final_cta' })}
              className="inline-flex items-center py-2.5 font-bold text-silver hover:text-bone transition-colors underline underline-offset-4 decoration-white/20 hover:decoration-bone"
            >
              Get Qualified Leads
            </Link>
            <Link
              to="/get-renovation-quotes"
              data-testid="final-cta-quotes"
              onClick={() => trackEvent(EVENTS.homeownerQuoteCta, { location: 'final_cta' })}
              className="inline-flex items-center py-2.5 font-bold text-silver hover:text-bone transition-colors underline underline-offset-4 decoration-white/20 hover:decoration-bone"
            >
              Get Renovation Quotes
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <SEO
        title="HyenaMind | Websites, Marketing & Leads for Contractors"
        description={SITE.description}
        path="/"
        jsonLd={[ORG_JSONLD, WEBSITE_JSONLD, faqLd(HOME_FAQS)]}
      />
      <Hero />
      <ChoosePathSection />
      <BigIdeaSection />
      <GrowthSystemSection />
      <AcquisitionSection />
      <Testimonials />
      <Suspense fallback={<div className="min-h-[56rem] bg-navy" aria-hidden="true" />}>
        <IndustriesExperience />
      </Suspense>
      <ContractorLeadsSection />
      <EcosystemBand />
      <HomeownerSection />
      <CategoriesSection />
      <Suspense fallback={<div className="min-h-[36rem] bg-bone" aria-hidden="true" />}>
        <HomeResources />
      </Suspense>

      <section className="bg-white border-t border-navy/10 py-24 sm:py-36" data-testid="home-faq-section">
        <div className="container-site grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>Questions</Overline>
              <h2 className="mt-5 font-display text-[2rem] sm:text-[2.35rem] tracking-tight leading-[1.08] font-medium text-navy">
                Straight answers, no jargon.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-navy/65">
                The questions contractors and homeowners actually ask us. Something else on your mind?{' '}
                <Link to="/contact" className="inline-flex items-center py-1.5 font-bold text-steel underline underline-offset-4" data-testid="faq-contact-link">
                  Talk to our team
                </Link>
                .
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <FAQ items={HOME_FAQS} testid="home-faq" />
          </div>
        </div>
      </section>

      <FinalCTASection />
      <StickyMobileCTA label="Grow Your Business" to="/contractors/grow-your-business" event={EVENTS.growthCta} />
      <GrowthPrompt />
    </>
  );
}
